import React, { useState, useEffect, useCallback } from "react";
import { X, Mail, FileText, Clock, UserPlus, Calendar, Package, CheckCircle, AlertCircle, Activity } from "react-feather";
import moment from "moment";
import agentActivityService from "../../../services/agentActivityService";
import { error as notifyError } from "../../../utils/notification";

import RequestQuoteModal from "./RequestQuoteModal";
import InviteSupplierModal from "./InviteSupplierModal";
import SendEmailModal from "./SendEmailModal";

interface IActivityEvent {
    type: string;
    title: string;
    description: string;
    timestamp: string;
    metadata: any;
    expanded: boolean;
}

interface IActivity {
    id: string;
    buyer_org_id: string;
    supplier_id: string;
    supplier_name: string;
    hubspot_id?: string;
    deal_id?: string;
    stage: string;
    stage_progress: number;
    stats: {
        emails_sent: number;
        quotes_received: number;
        pending: number;
        invited: number;
    };
    events: IActivityEvent[];
    created_at: string;
    updated_at: string;
}

interface IActivityDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    supplier: any;
}

const STAGE_STEPS = [
    { label: "Ingredient Need", key: "ingredient_need", progress: 0 },
    { label: "Supplier Match", key: "supplier_match", progress: 20 },
    { label: "Email Sent", key: "email_sent", progress: 40 },
    { label: "Quote Requested", key: "quote_requested", progress: 60 },
    { label: "Platform Invite", key: "platform_invite", progress: 80 },
    { label: "Connected", key: "connected", progress: 100 }
];

const ActivityDrawer: React.FC<IActivityDrawerProps> = ({ isOpen, onClose, supplier }) => {
    const [activity, setActivity] = useState<IActivity | null>(null);
    const [loading, setLoading] = useState(false);

    // Modal states
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const isSupplierMatched = React.useMemo(() => {
        return !!(
            activity?.id &&
            activity?.hubspot_id &&
            activity?.deal_id &&
            activity?.stats?.emails_sent === 0
        );
    }, [activity]);

    const displayProgress = React.useMemo(() => {
        const rawProgress = activity?.stage_progress || 0;
        // If matched but progress is lagging behind, show 20%
        if (isSupplierMatched && rawProgress < 20) {
            return 20;
        }
        return rawProgress;
    }, [activity, isSupplierMatched]);

    // Prevent infinite loop if init fails
    const [hasAttemptedInit, setHasAttemptedInit] = useState(false);

    // Reset attempt flag when modal opens with a new supplier
    useEffect(() => {
        if (isOpen) {
            setHasAttemptedInit(false);
        }
    }, [isOpen, supplier?._id]);

    const fetchActivity = useCallback(async () => {
        try {
            if (supplier?.activity_id) {
                // Fetch existing activity
                const response = await agentActivityService.getActivity(supplier.activity_id);
                setActivity(response.data);
            } else if (!hasAttemptedInit) {
                // Initialize new activity only once per session
                setHasAttemptedInit(true);
                const response = await agentActivityService.initAgentActivity({
                    supplier_name: supplier?.name || "Unknown Supplier",
                    supplier_email: supplier?.email || ""
                });
                setActivity(response.data);
                // Optionally update the supplier object with the new activity_id here if needed
                // supplier.activity_id = response.data.id;
            }
        } catch (err) {
            const error = err as any;
            console.error("Activity fetch/init error:", error);
            // Don't show toast on every poll failure if it's just missing
            if (error.response?.status === 403) {
                notifyError("You do not have permission to view this company's activity.");
            } else if (error.response?.status === 401) {
                console.error("Agent Init API requires authentication or token is invalid.");
            } else {
                console.error("Failed to initialize or fetch activity.", error);
            }
        }
    }, [supplier, hasAttemptedInit]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isOpen) {
            fetchActivity();
            // Only format polling if activity exists and isn't fully completed
            interval = setInterval(fetchActivity, 10000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isOpen, fetchActivity]);

    if (!isOpen) return null;

    const handleSendEmail = () => {
        console.log("Send Email clicked", { activityId: activity?.id, supplierActivityId: supplier?.activity_id });
        setIsEmailModalOpen(true);
    };

    const handleRequestQuote = () => {
        console.log("Request Quote clicked");
        setIsQuoteModalOpen(true);
    };

    const handleInviteToPlatform = () => {
        console.log("Invite to Platform clicked");
        setIsInviteModalOpen(true);
    };

    const handleScheduleFollowup = () => {
        // TODO: Implement Schedule Follow-up if needed, for now just show alert or similar
        console.log("Schedule Followup");
    };

    const handleActionSuccess = (updatedActivity: any) => {
        setActivity(updatedActivity);
    };

    const getEventIcon = (eventType: string) => {
        const type = (eventType || "").toLowerCase();
        if (type.includes("email")) return <Mail size={14} />;
        if (type.includes("quote")) return <FileText size={14} />;
        if (type.includes("invite") || type.includes("platform")) return <UserPlus size={14} />;
        if (type.includes("ingredient") || type.includes("sourcing")) return <Package size={14} />;
        if (type.includes("connected") || type.includes("complete")) return <CheckCircle size={14} />;
        if (type.includes("error") || type.includes("fail")) return <AlertCircle size={14} />;
        return <Activity size={14} />;
    };

    return (
        <>
            <div className={`activity-drawer-overlay ${isOpen ? "open" : ""}`} onClick={onClose}>
                <div className="activity-drawer-content" onClick={(e) => e.stopPropagation()}>
                    <div className="drawer-header">
                        <div className="header-left">
                            <div className="agent-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                </svg>
                            </div>
                            <div className="header-text">
                                <h2>Agent Activity</h2>
                                <p>{activity?.supplier_name || supplier?.name || "Journey Foods Test"}</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={onClose}>
                            <X size={24} />
                        </button>
                    </div>

                    <div className="drawer-body">
                        <div className="pipeline-section">
                            <div className="pipeline-header">
                                <span>Pipeline Progress</span>
                                <span className="progress-percent">{displayProgress}%</span>
                            </div>
                            <div className="agent-progress-rail">
                                <div className="agent-progress-fill" style={{ width: `${displayProgress}%` }}></div>
                            </div>
                            <div className="pipeline-steps">
                                {STAGE_STEPS.map((step) => {
                                    const isActive = displayProgress >= step.progress;
                                    return (
                                        <div key={step.key} className={`step ${isActive ? "active" : ""}`}>
                                            <div className="step-dot"></div>
                                            <span>{step.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="stats-grid">
                            <div className="stat-card">
                                <span className="stat-value">{activity?.stats.emails_sent || 0}</span>
                                <span className="stat-label">Emails</span>
                            </div>
                            <div className="stat-card">
                                <span className="stat-value">{activity?.stats.quotes_received || 0}</span>
                                <span className="stat-label">Quotes</span>
                            </div>
                            <div className="stat-card">
                                <span className="stat-value text-orange">{activity?.stats.pending || 0}</span>
                                <span className="stat-label">Pending</span>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon-wrapper">
                                    <span className="stat-value">{activity?.stats.invited || 0}</span>
                                    {activity?.stats.invited === 0 && <Clock size={24} className="text-orange" />}
                                </div>
                                <span className="stat-label">Invited</span>
                            </div>
                        </div>

                        <div className="activity-feed">
                            {activity?.events.map((event, index) => {
                                const eventTime = moment(event.timestamp);
                                const isInvalidDate = !event.timestamp || !eventTime.isValid();

                                return (
                                    <div key={index} className="activity-item">
                                        <div className="activity-icon-container">
                                            <div className={`activity-icon green`}>
                                                {getEventIcon(event.type)}
                                            </div>
                                            {index < activity.events.length - 1 && <div className="activity-line"></div>}
                                        </div>
                                        <div className="activity-details">
                                            <div className="activity-header-row">
                                                <span className="activity-tag">{event.type.replace(/_/g, " ")}</span>
                                                <span className="activity-time">
                                                    {isInvalidDate ? "–" : (
                                                        <>
                                                            {eventTime.format("D MMM, HH:mm")}
                                                            <span className="time-relative"> ({eventTime.fromNow()})</span>
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                            <h3 className="activity-title">{event.title}</h3>
                                            <p className="activity-desc">{event.description}</p>
                                            {event.metadata && Object.keys(event.metadata).length > 0 && (
                                                <button className="view-details-btn">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                                                        <path d="m6 9 6 6 6-6"></path>
                                                    </svg>
                                                    View details
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="drawer-footer">
                        <h3>QUICK ACTIONS</h3>
                        <div className="action-buttons">
                            <button className="action-btn primary" onClick={handleSendEmail}>
                                <Mail size={16} />
                                <span>Send Email</span>
                            </button>
                            <button className="action-btn secondary" onClick={handleRequestQuote}>
                                <FileText size={16} />
                                <span>Request Quote</span>
                            </button>
                            <button className="action-btn secondary" onClick={handleInviteToPlatform}>
                                <UserPlus size={16} />
                                <span>Invite to Platform</span>
                            </button>
                            <button className="action-btn secondary" onClick={handleScheduleFollowup}>
                                <Calendar size={16} />
                                <span>Schedule Follow-up</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {isOpen && (
                <>
                    <SendEmailModal
                        isOpen={isEmailModalOpen}
                        onClose={() => setIsEmailModalOpen(false)}
                        activityId={activity?.id || supplier?.activity_id || ""}
                        supplierEmail={supplier?.email || ""}
                        supplierName={activity?.supplier_name || supplier?.name}
                        onSuccess={handleActionSuccess}
                    />
                    <RequestQuoteModal
                        isOpen={isQuoteModalOpen}
                        onClose={() => setIsQuoteModalOpen(false)}
                        activityId={activity?.id || supplier?.activity_id || ""}
                        supplierEmail={supplier?.email || ""}
                        supplierName={activity?.supplier_name || supplier?.name}
                        onSuccess={handleActionSuccess}
                    />
                    <InviteSupplierModal
                        isOpen={isInviteModalOpen}
                        onClose={() => setIsInviteModalOpen(false)}
                        activityId={activity?.id || supplier?.activity_id || ""}
                        hubspotId={supplier?.hubspot_id || supplier?._id}
                        supplierEmail={supplier?.email || ""}
                        supplierName={activity?.supplier_name || supplier?.name}
                        onSuccess={handleActionSuccess}
                    />
                </>
            )}
        </>
    );
};

export default ActivityDrawer;
